import React, { useState, useEffect, useRef } from 'react';
import { getState, subscribe } from '../lib/userStore';
import { supabase } from '../lib/supabase';
import { interpretSearch } from '../lib/ai/aiService';

const PAGE_SIZE = 10;

// ─── Fallback provider data (used when Supabase is unavailable) ───────────────
const FALLBACK_PROVIDERS = [
  // PRIMARY CARE
  {
    id: 'hillcrest',
    name: 'Hillcrest Family Medicine',
    featured: true,
    specialty: 'Family Medicine · Primary Care',
    category: 'primary',
    tags: [],
    insurance: ['bcbs', 'aetna', 'medicaid'],
    status: 'accepting',
    phone: '(864) 455-1234', phoneTel: '8644551234',
    mapQuery: 'Hillcrest+Family+Medicine+Greenville+SC',
    mapsUrl: null, bookUrl: null, referral: 'none',
    address: null, hours: null,
    description: null, whoQualifies: null, whatToBring: null, insuranceNote: null,
    lat: 34.8526, lng: -82.3018,
  },
  {
    id: 'gim',
    name: 'Greenville Internal Medicine',
    featured: false,
    specialty: 'Internal Medicine · Primary Care',
    category: 'primary',
    tags: [],
    insurance: ['bcbs', 'cigna'],
    status: 'accepting',
    phone: '(864) 555-2001', phoneTel: '8645552001',
    mapQuery: 'Greenville+Internal+Medicine+Greenville+SC',
    mapsUrl: null, bookUrl: null, referral: 'none',
    address: null, hours: null,
    description: null, whoQualifies: null, whatToBring: null, insuranceNote: null,
    lat: 34.8420, lng: -82.3871,
  },
  {
    id: 'pelham',
    name: 'Pelham Medical Group',
    featured: false,
    specialty: 'Family Medicine · Primary Care',
    category: 'primary',
    tags: [],
    insurance: ['bcbs', 'aetna', 'united'],
    status: 'waitlist',
    phone: '(864) 555-2002', phoneTel: '8645552002',
    mapQuery: 'Pelham+Medical+Group+Greenville+SC',
    mapsUrl: null, bookUrl: null, referral: 'none',
    address: null, hours: null,
    description: null, whoQualifies: null, whatToBring: null, insuranceNote: null,
    lat: 34.8773, lng: -82.3368,
  },

  // URGENT CARE
  {
    id: 'prisma-uc-verdae',
    name: 'Prisma Health Urgent Care — Verdae',
    featured: false,
    specialty: 'Urgent Care · Walk-in',
    category: 'urgent',
    tags: [{ label: 'Walk-in', type: 'walkin' }],
    insurance: ['most'],
    status: 'open',
    phone: '(864) 455-1234', phoneTel: '8644551234',
    mapQuery: 'Prisma+Health+Urgent+Care+Verdae+Greenville+SC',
    mapsUrl: 'https://maps.apple.com/?address=1000+Verdae+Blvd+Greenville+SC',
    bookUrl: 'https://mychart.prismahealth.org/mychart/scheduling',
    referral: 'none',
    address: '1000 Verdae Blvd, Greenville, SC 29607',
    hours: 'Mon–Fri 8am–8pm · Sat–Sun 9am–5pm',
    description: 'Prisma Health Urgent Care at Verdae is a walk-in clinic handling non-life-threatening illnesses and injuries. No appointment needed. Services include treatment for colds, flu, sprains, minor lacerations, UTIs, and more. On-site X-ray and lab services available.',
    whoQualifies: ['Anyone with a non-emergency medical need', 'All ages including children', 'Most major insurance accepted — BCBS, Aetna, Medicare, Medicaid'],
    whatToBring: ['Photo ID', 'Insurance card', 'List of current medications'],
    insuranceNote: 'Accepts BCBS, Aetna, UHC, Cigna, Humana, Medicare, Medicaid. Self-pay options available.',
    lat: 34.8234, lng: -82.3237,
  },
  {
    id: 'fastmed',
    name: 'FastMed Urgent Care — Woodruff Rd',
    featured: false,
    specialty: 'Urgent Care · Walk-in · X-ray',
    category: 'urgent',
    tags: [{ label: 'Walk-in', type: 'walkin' }],
    insurance: ['most'],
    status: 'open',
    phone: '(864) 555-1234', phoneTel: '8645551234',
    mapQuery: 'FastMed+Urgent+Care+Woodruff+Greenville+SC',
    mapsUrl: 'https://maps.apple.com/?address=2146+Woodruff+Rd+Greenville+SC',
    bookUrl: null, referral: 'none',
    address: '2146 Woodruff Rd, Greenville, SC 29607',
    hours: 'Mon–Fri 8am–8pm · Sat–Sun 8am–6pm',
    description: 'FastMed on Woodruff Road is a walk-in urgent care with on-site X-ray and lab services. Consistently shorter waits than hospital ERs for non-emergency needs. Good for infections, rashes, minor injuries, flu, COVID testing, and sports physicals.',
    whoQualifies: ['Walk-in, no appointment needed', 'Adults and children 1+ year', 'Most insurance accepted'],
    whatToBring: ['Insurance card', 'Photo ID', 'Payment method for copay'],
    insuranceNote: 'Accepts most major insurance. Transparent self-pay pricing available — ask at the desk.',
    lat: 34.8198, lng: -82.3198,
  },
  {
    id: 'afc',
    name: 'AFC Urgent Care — Woodruff',
    featured: false,
    specialty: 'Urgent Care · Walk-in',
    category: 'urgent',
    tags: [{ label: 'Walk-in', type: 'walkin' }],
    insurance: ['most'],
    status: 'open',
    phone: '(864) 555-2004', phoneTel: '8645552004',
    mapQuery: 'AFC+Urgent+Care+Woodruff+Greenville+SC',
    mapsUrl: null, bookUrl: null, referral: 'none',
    address: null, hours: null,
    description: null, whoQualifies: null, whatToBring: null, insuranceNote: null,
    lat: 34.8187, lng: -82.3156,
  },

  // EMERGENCY ROOM
  {
    id: 'prisma-er',
    name: 'Prisma Health Greenville Memorial ER',
    featured: false,
    specialty: 'Emergency Room · 24/7',
    category: 'er',
    tags: [{ label: 'ER', type: 'er' }],
    insurance: ['all'],
    status: 'open',
    phone: '(864) 455-7000', phoneTel: '8644557000',
    mapQuery: 'Prisma+Health+Greenville+Memorial+Hospital+Greenville+SC',
    mapsUrl: null, bookUrl: null, referral: 'none',
    address: null, hours: '24 hours · 7 days a week',
    description: null, whoQualifies: null, whatToBring: null, insuranceNote: null,
    lat: 34.8354, lng: -82.3990,
  },

  // FREE & LOW-COST
  {
    id: 'neighborhood-health',
    name: 'Neighborhood Health',
    featured: false,
    specialty: 'Primary Care · Federally Qualified Health Center',
    category: 'free',
    tags: [{ label: 'Free', type: 'free' }, { label: 'Sliding scale', type: 'sliding' }],
    insurance: ['medicaid', 'medicare'],
    status: 'accepting',
    phone: '(864) 555-1111', phoneTel: '8645551111',
    mapQuery: 'Neighborhood+Health+Greenville+SC',
    mapsUrl: 'https://maps.apple.com/?address=703+Grove+Rd+Greenville+SC',
    bookUrl: null, referral: 'none',
    address: '703 Grove Rd, Greenville, SC 29605',
    hours: 'Mon–Fri 8am–5pm · Appointment preferred',
    description: 'Neighborhood Health is a Federally Qualified Health Center serving uninsured and underinsured patients across Greenville County. They operate on a sliding fee scale — you pay only what you can afford based on income. Services include primary care, women\'s health, behavioral health, dental, and pharmacy.',
    whoQualifies: ['Uninsured or underinsured adults and children', 'Income-based eligibility — most low-to-moderate income households qualify', 'No patient turned away for inability to pay'],
    whatToBring: ['Proof of income (pay stub, tax return, or self-declaration)', 'Photo ID if available', 'Any existing medical records helpful but not required'],
    insuranceNote: 'Accepts Medicaid, Medicare, and most insurance. Sliding scale for uninsured — as low as $0 depending on income.',
    lat: 34.8312, lng: -82.3987,
  },
  {
    id: 'free-medical-clinic',
    name: 'Free Medical Clinic of SC',
    featured: false,
    specialty: 'Primary Care · Volunteer-staffed',
    category: 'free',
    tags: [{ label: 'Free', type: 'free' }],
    insurance: [],
    status: 'accepting',
    phone: '(864) 555-2222', phoneTel: '8645552222',
    mapQuery: 'Free+Medical+Clinic+South+Carolina+Greenville',
    mapsUrl: 'https://maps.apple.com/?address=104+Edinburgh+Court+Greenville+SC',
    bookUrl: null, referral: 'none',
    address: '104 Edinburgh Court, Greenville, SC 29607',
    hours: 'Tuesday & Thursday evenings · By appointment',
    description: 'The Free Medical Clinic of SC provides no-cost primary care and specialist referrals to income-eligible adults who are uninsured. Staffed entirely by volunteer physicians, nurses, and nurse practitioners. The quality of care here is genuinely good.',
    whoQualifies: ['Uninsured adults 18+ in Greenville County', 'Income at or below 200% of federal poverty level', 'Not eligible for Medicaid or Medicare'],
    whatToBring: ['Photo ID', 'Proof of income', 'Proof of Greenville County residency', 'List of current medications'],
    insuranceNote: 'Free of charge — no insurance required or accepted. Income verification required.',
    lat: 34.8523, lng: -82.3312,
  },

  // MENTAL HEALTH
  {
    id: 'gmhc',
    name: 'Greenville Mental Health Center',
    featured: false,
    specialty: 'Mental Health · Counseling · Substance Use',
    category: 'mental-health',
    tags: [{ label: 'Sliding scale', type: 'mental' }],
    insurance: ['medicaid', 'medicare'],
    status: 'accepting',
    phone: '(864) 555-3333', phoneTel: '8645553333',
    mapQuery: 'Greenville+Mental+Health+Center+Greenville+SC',
    mapsUrl: 'https://maps.apple.com/?address=124+Calhoun+St+Greenville+SC',
    bookUrl: null, referral: 'none',
    address: '124 Calhoun St, Greenville, SC 29601',
    hours: 'Mon–Fri 8am–5pm · Appointment required',
    description: 'The Greenville Mental Health Center is a state-operated outpatient facility providing mental health and substance use treatment services to adults and children. Services include individual therapy, psychiatric evaluation, medication management, case management, and crisis services.',
    whoQualifies: ['Adults and children with mental health or substance use needs', 'Sliding scale based on income — no one turned away', 'Priority given to those with serious mental illness'],
    whatToBring: ['Photo ID', 'Insurance card if insured', 'Proof of income for sliding scale determination'],
    insuranceNote: 'Accepts Medicaid, Medicare, and most private insurance. Sliding scale for uninsured.',
    lat: 34.8489, lng: -82.4012,
  },
  {
    id: '988',
    name: '988 Suicide & Crisis Lifeline',
    featured: false,
    specialty: 'Crisis Support · Call or text 988',
    category: 'mental-health',
    tags: [{ label: 'Free', type: 'free' }, { label: 'Crisis line', type: 'mental' }, { label: '24/7', type: 'mental' }],
    insurance: ['all'],
    status: 'open',
    phone: '988', phoneTel: '988',
    mapQuery: null,
    mapsUrl: null, bookUrl: null, referral: 'none',
    address: null, hours: '24 hours a day, 7 days a week',
    description: '988 is the national suicide and crisis lifeline. Call or text 988 from any phone to reach a trained crisis counselor — free, confidential, and available around the clock. You don\'t have to be suicidal to call. 988 supports anyone in emotional distress or mental health crisis.',
    whoQualifies: ['Anyone experiencing a mental health crisis', 'Anyone feeling suicidal, hopeless, or overwhelmed', 'Family members or friends concerned about a loved one'],
    whatToBring: ['Nothing — just call or text 988'],
    insuranceNote: 'Completely free. No insurance or ID required.',
    lat: null, lng: null,
  },

  // SPECIALISTS
  {
    id: 'upstate-derm',
    name: 'Upstate Dermatology',
    featured: true,
    specialty: 'Dermatology',
    category: 'specialist',
    tags: [],
    insurance: ['bcbs', 'aetna'],
    status: 'accepting',
    phone: '(864) 555-2005', phoneTel: '8645552005',
    mapQuery: 'Upstate+Dermatology+Greenville+SC',
    mapsUrl: null, bookUrl: null, referral: 'varies',
    address: null, hours: null,
    description: null, whoQualifies: null, whatToBring: null, insuranceNote: null,
    lat: 34.8521, lng: -82.3314,
  },
  {
    id: 'gsc',
    name: 'Greenville Skin Care Center',
    featured: false,
    specialty: 'Dermatology',
    category: 'specialist',
    tags: [],
    insurance: ['bcbs', 'aetna'],
    status: 'not-accepting',
    phone: '(864) 555-2006', phoneTel: '8645552006',
    mapQuery: 'Greenville+Skin+Care+Center+Greenville+SC',
    mapsUrl: null, bookUrl: null, referral: 'varies',
    address: null, hours: null,
    description: null, whoQualifies: null, whatToBring: null, insuranceNote: null,
    lat: 34.8476, lng: -82.3387,
  },
  {
    id: 'bonsecours-cardio',
    name: 'Bon Secours St Francis Cardiology',
    featured: false,
    specialty: 'Cardiology',
    category: 'specialist',
    tags: [],
    insurance: ['bcbs', 'medicare'],
    status: 'accepting',
    phone: '(864) 555-2007', phoneTel: '8645552007',
    mapQuery: 'Bon+Secours+St+Francis+Cardiology+Greenville+SC',
    mapsUrl: null, bookUrl: null, referral: 'required',
    address: null, hours: null,
    description: null, whoQualifies: null, whatToBring: null, insuranceNote: null,
    lat: 34.8612, lng: -82.3954,
  },
  {
    id: 'goa',
    name: 'Greenville Orthopedic Associates',
    featured: false,
    specialty: 'Orthopedics',
    category: 'specialist',
    tags: [],
    insurance: ['bcbs', 'aetna', 'united'],
    status: 'accepting',
    phone: '(864) 555-2008', phoneTel: '8645552008',
    mapQuery: 'Greenville+Orthopedic+Associates+Greenville+SC',
    mapsUrl: null, bookUrl: null, referral: 'ppo-optional-hmo-required',
    address: null, hours: null,
    description: null, whoQualifies: null, whatToBring: null, insuranceNote: null,
    lat: 34.8298, lng: -82.2987,
  },
  {
    id: 'prisma-obgyn',
    name: 'Prisma Health OB/GYN',
    featured: false,
    specialty: "OB/GYN · Women's Health",
    category: 'specialist',
    tags: [],
    insurance: ['bcbs', 'medicaid'],
    status: 'accepting',
    phone: '(864) 455-2010', phoneTel: '8644552010',
    mapQuery: 'Prisma+Health+OBGYN+Greenville+SC',
    mapsUrl: null,
    bookUrl: 'https://mychart.prismahealth.org/mychart/scheduling',
    referral: 'none',
    address: null, hours: null,
    description: null, whoQualifies: null, whatToBring: null, insuranceNote: null,
    lat: 34.8401, lng: -82.3876,
  },

  // PHYSICAL THERAPY
  {
    id: 'headspace-pt',
    name: 'Headspace Physical Therapy',
    featured: false,
    specialty: 'Physical Therapy',
    category: 'pt',
    tags: [],
    insurance: ['bcbs', 'aetna'],
    status: 'accepting',
    phone: '(864) 555-2009', phoneTel: '8645552009',
    mapQuery: 'Headspace+Physical+Therapy+Greenville+SC',
    mapsUrl: null, bookUrl: null, referral: 'hmo-only',
    address: null, hours: null,
    description: null, whoQualifies: null, whatToBring: null, insuranceNote: null,
    lat: 34.8334, lng: -82.3521,
  },
];

// ─── DB ↔ UI category maps ────────────────────────────────────────────────────
const FILTER_TO_CATEGORY = {
  primary:         'primary-care',
  urgent:          'urgent-care',
  er:              'er',
  specialist:      'specialist',
  'mental-health': 'mental-health',
  pt:              'physical-therapy',
  dental:          'dental',
  imaging:         'imaging',
  lab:             'lab',
  holistic:        'holistic',
};

const DB_TO_UI = {
  'primary-care':     'primary',
  'urgent-care':      'urgent',
  'er':               'er',
  'specialist':       'specialist',
  'mental-health':    'mental-health',
  'physical-therapy': 'pt',
  'free-low-cost':    'free',
  'dental':           'dental',
  'imaging':          'imaging',
  'lab':              'lab',
  'holistic':         'holistic',
};

// ─── Lookup tables ─────────────────────────────────────────────────────────────
const CARRIER_DISPLAY = { bcbs: 'BCBS', aetna: 'Aetna', cigna: 'Cigna', united: 'UnitedHealth', humana: 'Humana', medicaid: 'Medicaid', medicare: 'Medicare' };
const PLAN_DISPLAY    = { ppo: 'PPO', hmo: 'HMO', hdhp: 'HDHP', epo: 'EPO' };


const OPEN_NOW_STYLE   = { background: '#f0f7f5', color: '#1e5c54' };
const CLOSED_NOW_STYLE = { background: '#fef3f0', color: '#c0392b' };

const TAG_STYLES = {
  walkin:  { background: '#fef0f0', color: '#c0392b' },
  er:      { background: '#fef0f0', color: '#c0392b' },
  free:    { background: 'var(--teal-faint)', color: 'var(--teal)' },
  sliding: { background: 'var(--amber-light)', color: 'var(--amber)' },
  mental:  { background: '#f0eef8', color: '#7c6fad' },
};

const CATEGORY_COLORS = {
  primary:        'var(--teal)',
  urgent:         '#c0392b',
  er:             '#c0392b',
  free:           'var(--teal)',
  'mental-health':'#7c6fad',
  specialist:     'var(--teal)',
  pt:             'var(--teal)',
  dental:         'var(--teal)',
  imaging:        'var(--teal)',
  lab:            'var(--teal)',
  holistic:       'var(--amber)',
};

const CATEGORY_LABELS = {
  primary:        'Primary Care',
  urgent:         'Urgent Care',
  er:             'Emergency Room',
  free:           'Free & Low-Cost Care',
  'mental-health':'Mental Health',
  specialist:     'Specialist',
  pt:             'Physical Therapy',
  dental:         'Dental',
  imaging:        'Imaging & Radiology',
  lab:            'Lab & Diagnostics',
  holistic:       'Holistic & Integrative',
};

const PRIMARY_PILLS = [
  { id: 'all',           label: 'All' },
  { id: 'saved',         label: 'Saved' },
  { id: 'primary',       label: 'Primary care' },
  { id: 'urgent',        label: 'Urgent care' },
  { id: 'er',            label: 'ER' },
  { id: 'specialist',    label: 'Specialists' },
  { id: 'mental-health', label: 'Mental health' },
];

const RARE_CATEGORIES = [
  { id: 'dental',   label: 'Dental' },
  { id: 'pt',       label: 'Physical therapy' },
  { id: 'lab',      label: 'Lab & diagnostics' },
  { id: 'imaging',  label: 'Imaging' },
  { id: 'holistic', label: 'Holistic' },
  { id: 'free',     label: 'Free & low-cost' },
];

const RARE_IDS = new Set(RARE_CATEGORIES.map(c => c.id));

// Specialist sub-categories — shown in bottom sheet, grouped by body area
// `match` is used as an ILIKE filter against the specialty column
const SPECIALTY_GROUPS = [
  { group: 'Heart & Vascular', specialties: [
    { label: 'Cardiology',      match: 'cardiovascular' },
  ]},
  { group: 'Bones & Joints', specialties: [
    { label: 'Orthopedics',     match: 'orthopedic' },
    { label: 'Rheumatology',    match: 'rheumatology' },
  ]},
  { group: "Women's Health", specialties: [
    { label: 'OB/GYN',          match: 'obstetrics' },
  ]},
  { group: 'Digestive', specialties: [
    { label: 'Gastroenterology', match: 'gastroenterology' },
  ]},
  { group: 'Brain & Nerves', specialties: [
    { label: 'Neurology',       match: 'neurology' },
  ]},
  { group: 'Hormones & Metabolic', specialties: [
    { label: 'Endocrinology',   match: 'endocrinology' },
    { label: 'Urology',         match: 'urology' },
  ]},
  { group: 'Skin', specialties: [
    { label: 'Dermatology',     match: 'dermatology' },
  ]},
  { group: 'Eyes & Vision', specialties: [
    { label: 'Ophthalmology',   match: 'ophthalmology' },
    { label: 'Optometry',       match: 'optometrist' },
  ]},
];

// ─── Helper functions ──────────────────────────────────────────────────────────
function getInitialFilter(ctx) {
  if (!ctx) return 'common';
  const { chips = [], resultLevel, careType } = ctx;
  // Normalize careType — AI may return variations ("primary care" vs "primary-care")
  const care = (careType || '').toLowerCase().replace(/\s+/g, '-');
  if (care === 'er' || care === 'urgent-care') return 'urgent-er';
  if (care === 'primary-care' || care === 'primary') return 'primary';
  if (care === 'specialist') return 'specialist';
  if (care === 'mental-health') return 'mental-health';
  // Legacy chip-based routing
  if (['Chest pain', 'Shortness of breath'].some(c => chips.includes(c)) && resultLevel === 'urgent') return 'urgent-er';
  if (['Fever', 'Cough', 'Sore throat', 'Headache', 'Stomach pain', 'Rash'].some(c => chips.includes(c)) && resultLevel === 'soon') return 'primary';
  return 'all';
}

function insuranceMatches(provider, carrier) {
  if (!provider.insurance || !provider.insurance.length) return false;
  if (provider.insurance.includes('all') || provider.insurance.includes('most')) return true;
  if (!carrier) return false;
  return provider.insurance.includes(carrier);
}

function insMatchLabel(provider, carrier, planType) {
  if (provider.insurance.includes('all')) return 'Takes all insurance';
  if (provider.insurance.includes('most')) return 'Takes most insurance';
  const c = CARRIER_DISPLAY[carrier] || '';
  const p = PLAN_DISPLAY[planType] || '';
  return `Takes your ${c}${p ? ` ${p}` : ''}`;
}

function getRefTag(referral, planType) {
  if (referral === 'none') return null;
  if (referral === 'required') return 'Referral required';
  if (referral === 'varies') return 'Referral varies';
  if (referral === 'hmo-only') return (planType === 'hmo') ? 'Referral required' : null;
  if (referral === 'ppo-optional-hmo-required') {
    if (planType === 'hmo' || planType === 'epo') return 'Referral required';
    if (planType === 'ppo') return 'Referral optional';
    return 'Referral varies';
  }
  return null;
}

// Haversine formula — returns distance in miles
function haversine(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
}

function getProviderDistance(p, userLoc) {
  if (!userLoc) return null;
  if (p.lat != null && p.lng != null) return haversine(userLoc.lat, userLoc.lng, p.lat, p.lng);
  return null;
}

function sortProviders(list, distances, sortBy = 'distance') {
  // Split into two tiers: providers with a known distance, and those without
  const located   = list.filter(p => distances && distances[p.id] != null);
  const unlocated = list.filter(p => !distances || distances[p.id] == null);

  const sortTier = (tier) => [...tier].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    if (distances) {
      const da = distances[a.id] ?? 9999;
      const db = distances[b.id] ?? 9999;
      return da - db;
    }
    return a.name.localeCompare(b.name);
  });

  return [...sortTier(located), ...sortTier(unlocated)];
}

function applySearch(providers, search) {
  if (!search.trim()) return providers;
  const q = search.trim().toLowerCase();
  return providers.filter(p =>
    p.name.toLowerCase().includes(q) || p.specialty.toLowerCase().includes(q)
  );
}

// ─── Hours helpers ─────────────────────────────────────────────────────────────
const DAY_KEYS   = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const WEEK_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_SHORT  = { sunday: 'Sun', monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat' };
const DAY_FULL   = { sunday: 'Sunday', monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday' };

function getTodayHours(row) {
  const day = DAY_KEYS[new Date().getDay()];
  return row[`hours_${day}`] || null;
}

// Returns true (open), false (closed), or null (unknown)
function isOpenNow(hoursStr) {
  if (!hoursStr) return null;
  if (hoursStr === 'Closed') return false;
  if (hoursStr === 'Open 24 hours') return true;
  // Parse "9:00 AM – 5:00 PM" (en-dash from Google)
  const match = hoursStr.match(/(\d+:\d+\s*[AP]M)\s*[–-]\s*(\d+:\d+\s*[AP]M)/i);
  if (!match) return null;
  const toMinutes = str => {
    const [time, period] = str.trim().split(/\s+/);
    let [h, m] = time.split(':').map(Number);
    if (period.toUpperCase() === 'PM' && h !== 12) h += 12;
    if (period.toUpperCase() === 'AM' && h === 12) h = 0;
    return h * 60 + m;
  };
  const now = new Date().getHours() * 60 + new Date().getMinutes();
  return now >= toMinutes(match[1]) && now < toMinutes(match[2]);
}

function shortAddress(addr) {
  if (!addr) return null;
  const parts = addr.split(',');
  if (parts.length >= 2) return `${parts[0].trim()}, ${parts[1].trim()}`;
  return addr;
}

// Returns "Opens Mon at 9:00 AM" or "Opens today at 9:00 AM" when closed
function getNextOpen(allHours) {
  if (!allHours) return null;
  const todayIdx = new Date().getDay();
  const toMin = str => {
    const [time, period] = str.trim().split(/\s+/);
    let [h, m] = time.split(':').map(Number);
    if (period.toUpperCase() === 'PM' && h !== 12) h += 12;
    if (period.toUpperCase() === 'AM' && h === 12) h = 0;
    return h * 60 + m;
  };
  // Opens later today?
  const todayH = allHours[DAY_KEYS[todayIdx]];
  if (todayH && todayH !== 'Closed' && todayH !== 'Open 24 hours') {
    const m = todayH.match(/(\d+:\d+\s*[AP]M)\s*[–-]/i);
    if (m && new Date().getHours() * 60 + new Date().getMinutes() < toMin(m[1]))
      return `Opens today at ${m[1]}`;
  }
  // Next open day
  for (let i = 1; i <= 7; i++) {
    const idx = (todayIdx + i) % 7;
    const h = allHours[DAY_KEYS[idx]];
    if (h && h !== 'Closed') {
      const m = h.match(/(\d+:\d+\s*[AP]M)/i);
      if (m) return `Opens ${DAY_SHORT[DAY_KEYS[idx]]} at ${m[1]}`;
    }
  }
  return null;
}

// ─── Row adapter: Supabase row → UI provider shape ────────────────────────────
function formatPhone(raw) {
  if (!raw) return null;
  const d = String(raw).replace(/\D/g, '');
  if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  return String(raw);
}

function adaptRow(row) {
  // providers_v2 column names — display_name is the canonical name field
  const displayName = (row.google_place_name || row.display_name || row.practice_name || '').trim() || 'Unknown Provider';
  const phone       = formatPhone(row.phone);
  const phoneTel    = String(row.phone || '').replace(/\D/g, '');
  // providers_v2 uses site_of_care_type instead of category
  const cat         = row.site_of_care_type || row.category || '';
  const tags        = cat === 'urgent-care' ? [{ label: 'Walk-in', type: 'walkin' }]
                    : cat === 'er'          ? [{ label: 'ER', type: 'er' }]
                    : [];
  return {
    // providers_v2 uses provider_id as PK; fall back to npi for legacy rows
    id:            String(row.provider_id || row.npi || row.id),
    name:          displayName,
    featured:      !!row.is_featured,
    specialty:     row.specialty_primary || row.specialty || '',
    category:      DB_TO_UI[cat] || cat,
    tags,
    // accepted_payers is the denormalized array maintained by the trigger;
    // the insurance matching functions read this directly
    insurance:     Array.isArray(row.accepted_payers) ? row.accepted_payers : [],
    status:        row.accepting_patients === false ? 'not-accepting' : 'accepting',
    phone,
    phoneTel,
    mapQuery:      displayName.replace(/\s+/g, '+') + '+' + (row.city || 'Greenville') + '+' + (row.state || 'SC'),
    mapsUrl:       row.maps_url || null,
    bookUrl:       row.booking_url || null,
    referral:      (['primary-care', 'urgent-care', 'er', 'free-low-cost', 'mental-health'].includes(cat))
                   ? 'none'
                   : cat === 'specialist'
                     ? (row.referral_required ? 'ppo-optional-hmo-required' : 'none')
                     : (row.referral_required ? 'required' : 'none'),
    address:        row.google_formatted_address
                    || [row.address_line1, row.city, row.state, row.zip].filter(Boolean).join(', ')
                    || null,
    todayHours:     getTodayHours(row),
    openNow:        isOpenNow(getTodayHours(row)),
    businessStatus: row.business_status || null,
    allHours: {
      monday:    row.hours_monday    || null,
      tuesday:   row.hours_tuesday   || null,
      wednesday: row.hours_wednesday || null,
      thursday:  row.hours_thursday  || null,
      friday:    row.hours_friday    || null,
      saturday:  row.hours_saturday  || null,
      sunday:    row.hours_sunday    || null,
    },
    website:        row.website_url || null,
    hours:          null,
    description:    row.description   || null,
    whoQualifies:   row.who_qualifies  || null,
    whatToBring:    row.what_to_bring  || null,
    insuranceNote:  row.insurance_note || null,
    telehealth:     !!row.telehealth_available,
    sameDay:        !!row.same_day_capable,
    onlineBooking:  !!row.online_booking,
    lat:            row.lat ?? null,
    lng:            row.lng ?? null,
    // providers_v2 has no geocode_status or is_practice columns;
    // all migrated rows are visible practices by definition
    geocodeStatus:  null,
    dbId:           row.provider_id ?? null,
    isPractice:     true,
  };
}

function toTitleCase(str) {
  if (!str) return str;
  return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

// When the same practice name appears more than once, append specialty to disambiguate.
// e.g. "Greenville Medical Associates" → "Greenville Medical Associates — Family Medicine"
function disambiguateNames(providers) {
  const counts = {};
  providers.forEach(p => { counts[p.name] = (counts[p.name] || 0) + 1; });
  return providers.map(p =>
    counts[p.name] > 1 && p.specialty
      ? { ...p, name: `${p.name} — ${p.specialty}` }
      : p
  );
}

// ─── Supabase query builders ───────────────────────────────────────────────────
function applyFilterQuery(q, filter, specialtyMatches = [], extras = {}) {
  q = q.eq('is_visible', true);
  if (extras.telehealth)     q = q.eq('telehealth_available', true);
  if (extras.pediatric)      q = q.eq('sees_pediatric', true);
  if (extras.same_day)       q = q.eq('same_day_capable', true);      // providers_v2 column name
  if (extras.online_booking) q = q.eq('online_booking', true);

  // Multi-category from AI (overrides single filter)
  if (extras.categories && extras.categories.length > 0) {
    return q.in('site_of_care_type', extras.categories);              // providers_v2 column name
  }

  if (filter === 'common')    return q.in('site_of_care_type', ['primary-care', 'urgent-care', 'er']);
  if (filter === 'urgent-er') return q.in('site_of_care_type', ['urgent-care', 'er']);
  if (filter === 'specialist') {
    q = q.eq('site_of_care_type', 'specialist');
    if (specialtyMatches.length > 0)
      q = q.or(specialtyMatches.map(m => `specialty_primary.ilike.%${m}%`).join(','));
    return q;
  }
  const cat = FILTER_TO_CATEGORY[filter];
  if (cat) return q.eq('site_of_care_type', cat);
  return q; // 'all' — no category filter
}

function buildGeoQuery(filter, userLoc, specialtyMatches = [], extras = {}) {
  const delta    = 15 / 69;
  const deltaLng = 15 / (69 * Math.cos(userLoc.lat * Math.PI / 180));
  // providers_v2: lat/lng columns (not latitude/longitude); no is_practice column
  let q = supabase.from('providers_v2').select('*')
    .gte('lat', userLoc.lat - delta)
    .lte('lat', userLoc.lat + delta)
    .gte('lng', userLoc.lng - deltaLng)
    .lte('lng', userLoc.lng + deltaLng);
  return applyFilterQuery(q, filter, specialtyMatches, extras);
}

function buildUnlocatedQuery(filter, specialtyMatches = [], extras = {}) {
  // providers_v2 has no geocode_status column; query providers with null lat/lng instead
  let q = supabase.from('providers_v2').select('*')
    .is('lat', null)
    .order('display_name')
    .limit(200);
  return applyFilterQuery(q, filter, specialtyMatches, extras);
}

function buildPageQuery(filter, skip, specialtyMatches = [], extras = {}) {
  let q = supabase.from('providers_v2')
    .select('*', { count: 'exact' })
    .order('is_featured', { ascending: false })
    .order('display_name')
    .range(skip, skip + PAGE_SIZE - 1);
  return applyFilterQuery(q, filter, specialtyMatches, extras);
}

// ─── Saved providers (localStorage) ──────────────────────────────────────────
function loadSavedProviders() {
  try { return JSON.parse(localStorage.getItem('cp_saved_providers') || '[]'); } catch { return []; }
}
function persistSavedProviders(providers) {
  try { localStorage.setItem('cp_saved_providers', JSON.stringify(providers)); } catch {}
}

// ─── Provider card ─────────────────────────────────────────────────────────────
function ProviderCard({ provider, carrier, planType, onTap, distance, isSaved, onToggleSave }) {
  const [showHours, setShowHours] = useState(false);
  const matches    = insuranceMatches(provider, carrier);
  const matchLabel = matches ? insMatchLabel(provider, carrier, planType) : null;
  const refTag     = getRefTag(provider.referral, planType);
  const distLabel  = distance != null ? `${distance.toFixed(1)} mi` : null;
  const shortAddr  = shortAddress(provider.address);
  const hasHours   = provider.allHours && Object.values(provider.allHours).some(Boolean);
  const nextOpen   = provider.openNow === false ? getNextOpen(provider.allHours) : null;
  const todayKey   = DAY_KEYS[new Date().getDay()];

  return (
    <div style={{ ...s.card, ...(provider.featured ? s.cardFeatured : {}) }}>
      {provider.featured && (
        <div style={s.featuredBar}>
          <div style={s.featuredDot} />
          <span style={s.featuredLabel}>Featured provider</span>
        </div>
      )}
      <div style={s.cardBody} onClick={onTap}>
        <div style={s.cardTop}>
          <div style={s.cardLeft}>
            <div style={s.cardName}>{provider.name}</div>
            <div style={s.cardSpecialty}>{provider.specialty}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            {(provider.status === 'accepting' || provider.status === 'waitlist' || provider.status === 'not-accepting') && (
              <div style={{
                ...s.statusBadge,
                background: provider.status === 'accepting' ? '#f0f7f5' : provider.status === 'waitlist' ? '#fdf0e6' : '#fef3f0',
                color: provider.status === 'accepting' ? 'var(--teal)' : provider.status === 'waitlist' ? 'var(--amber)' : '#c0392b',
              }}>
                {provider.status === 'accepting' ? 'Accepting' : provider.status === 'waitlist' ? 'Waitlist' : 'Not accepting'}
              </div>
            )}
            <button
              style={{ ...s.heartBtn, color: isSaved ? '#c0392b' : 'var(--ink-4)' }}
              onClick={e => { e.stopPropagation(); onToggleSave(provider); }}
              aria-label={isSaved ? 'Unsave provider' : 'Save provider'}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill={isSaved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 13.5S1.5 9.5 1.5 5.5a3 3 0 015.5-1.65A3 3 0 0114.5 5.5c0 4-6.5 8-6.5 8z"/>
              </svg>
            </button>
          </div>
        </div>

        {(shortAddr || distLabel) && (
          <div style={s.cardAddress}>
            {shortAddr && <span>{shortAddr}</span>}
            {shortAddr && distLabel && <span style={{ color: 'var(--ink-4)' }}> · </span>}
            {distLabel && <span>{distLabel}</span>}
          </div>
        )}

        {matches && (
          <div style={s.insMatch}>
            <div style={s.insCheck}>
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                <path d="M1.5 4l2 2 3-3" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span style={s.insLabel}>{matchLabel}</span>
          </div>
        )}

        <div style={s.tags}>
          {provider.businessStatus === 'CLOSED_TEMPORARILY' && (
            <span style={{ ...s.tag, ...CLOSED_NOW_STYLE }}>Temporarily closed</span>
          )}
          {provider.businessStatus !== 'CLOSED_TEMPORARILY' && provider.openNow === true && (
            <button style={{ ...s.tag, ...OPEN_NOW_STYLE, ...(hasHours ? s.tagClickable : {}) }}
              onClick={hasHours ? e => { e.stopPropagation(); setShowHours(h => !h); } : undefined}
            >
              Open now{hasHours ? (showHours ? ' ▲' : ' ▾') : ''}
            </button>
          )}
          {provider.businessStatus !== 'CLOSED_TEMPORARILY' && provider.openNow === false && (
            <button style={{ ...s.tag, ...CLOSED_NOW_STYLE, ...(hasHours ? s.tagClickable : {}) }}
              onClick={hasHours ? e => { e.stopPropagation(); setShowHours(h => !h); } : undefined}
            >
              {nextOpen || 'Closed'}{hasHours ? (showHours ? ' ▲' : ' ▾') : ''}
            </button>
          )}
          {provider.tags.map(t => (
            <span key={t.label} style={{ ...s.tag, ...TAG_STYLES[t.type] }}>{t.label}</span>
          ))}
          {refTag && <span style={s.tagReferral}>{refTag}</span>}
        </div>

        {showHours && hasHours && (
          <div style={s.hoursPanel} onClick={e => e.stopPropagation()}>
            {WEEK_ORDER.map(day => !provider.allHours[day] ? null : (
              <div key={day} style={{ ...s.hoursRow, ...(day === todayKey ? s.hoursTodayRow : {}) }}>
                <span style={s.hoursDay}>{DAY_FULL[day]}</span>
                <span style={s.hoursTime}>{provider.allHours[day]}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={s.cardActions}>
        <a href={`tel:${provider.phoneTel}`} style={s.action}>Call</a>
        {provider.mapQuery && (
          <>
            <div style={s.actionDivider} />
            <button style={s.action} onClick={() => window.open('https://www.google.com/maps/search/' + provider.mapQuery)}>Directions</button>
          </>
        )}
        {(provider.bookUrl || provider.website) && (
          <>
            <div style={s.actionDivider} />
            <button style={{ ...s.action, color: 'var(--teal)', fontWeight: 600 }} onClick={() => window.open(provider.bookUrl || provider.website)}>
              {provider.bookUrl ? 'Book' : 'Website'}
            </button>
          </>
        )}
        {!provider.mapQuery && !provider.bookUrl && !provider.website && (
          <>
            <div style={s.actionDivider} />
            <button style={s.action} onClick={onTap}>More info</button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Detail view ───────────────────────────────────────────────────────────────
function DetailView({ provider, carrier, planType, onBack }) {
  const categoryColor = CATEGORY_COLORS[provider.category] || 'var(--teal)';
  const categoryLabel = CATEGORY_LABELS[provider.category] || '';
  const matches    = insuranceMatches(provider, carrier);
  const matchLabel = matches ? insMatchLabel(provider, carrier, planType) : null;
  const refTag     = getRefTag(provider.referral, planType);
  const hasRichDetail = !!(provider.description);

  const [linkedProviders, setLinkedProviders] = useState([]);
  useEffect(() => {
    if (!provider.isPractice || !provider.dbId) return;
    supabase
      .from('providers')
      .select('id, name, specialty, accepting_new_patients, gender')
      .eq('practice_id', provider.dbId)
      .order('name')
      .then(({ data }) => { if (data?.length) setLinkedProviders(data); });
  }, [provider.dbId, provider.isPractice]);

  const callIcon = (
    <svg width="15" height="15" viewBox="0 0 14 14" fill="none">
      <path d="M2 2h3l1.5 3.5-2 1.2a8 8 0 003.8 3.8l1.2-2L13 10v3a1 1 0 01-1 1C5.4 14 0 8.6 0 2a1 1 0 011-1h1z" fill="currentColor"/>
    </svg>
  );

  const pinIcon = (
    <svg width="15" height="15" viewBox="0 0 14 14" fill="none">
      <path d="M7 0C4.24 0 2 2.24 2 5c0 3.75 5 9 5 9s5-5.25 5-9c0-2.76-2.24-5-5-5zm0 6.5C6.17 6.5 5.5 5.83 5.5 5S6.17 3.5 7 3.5 8.5 4.17 8.5 5 7.83 6.5 7 6.5z" fill="currentColor"/>
    </svg>
  );

  return (
    <div style={s.screen}>
      <div style={s.detailHeader}>
        <div style={{ ...s.accentBar, background: `linear-gradient(90deg, ${categoryColor}, ${categoryColor}99)` }} />
        <button style={s.back} onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 4L6 8l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back to results
        </button>
      </div>

      <div style={s.body}>
        {/* Hero */}
        <div style={s.detailHero}>
          <div style={{ ...s.categoryLabel, color: categoryColor }}>{categoryLabel}</div>
          <div style={s.detailName}>{provider.name}</div>
          <div style={s.detailSpecialty}>{provider.specialty}</div>
          <div style={s.tagRow}>
            {provider.tags.map(t => (
              <span key={t.label} style={{ ...s.detailTag, ...TAG_STYLES[t.type] }}>{t.label}</span>
            ))}
          </div>
        </div>

        {/* Quick actions */}
        <div style={s.quickActions}>
          <a href={`tel:${provider.phoneTel}`} style={{ ...s.actionPill, background: 'var(--teal)', color: 'white' }}>
            {callIcon}
            {provider.phone}
          </a>
          {provider.mapsUrl && (
            <a href={provider.mapsUrl} target="_blank" rel="noreferrer" style={{ ...s.actionPill, background: '#e8f0fe', color: '#1a56c4' }}>
              {pinIcon}
              Directions
            </a>
          )}
          {provider.mapQuery && !provider.mapsUrl && (
            <button style={{ ...s.actionPill, background: '#e8f0fe', color: '#1a56c4' }} onClick={() => window.open('https://www.google.com/maps/search/' + provider.mapQuery)}>
              {pinIcon}
              Directions
            </button>
          )}
          {provider.bookUrl && (
            <button style={{ ...s.actionPill, background: 'var(--teal-faint)', color: 'var(--teal)' }} onClick={() => window.open(provider.bookUrl)}>
              Book online
            </button>
          )}
        </div>

        {/* Insurance match */}
        {(matches || refTag) && (
          <div style={s.detailSection}>
            <div style={s.detailSectionLabel}>Insurance</div>
            {matches && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: refTag ? '8px' : 0 }}>
                <div style={s.insCheck}>
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                    <path d="M1.5 4l2 2 3-3" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span style={{ fontSize: '13.5px', color: 'var(--teal)', fontWeight: 500 }}>{matchLabel}</span>
              </div>
            )}
            {refTag && (
              <div style={{ fontSize: '13px', color: 'var(--amber)', fontWeight: 500 }}>{refTag}</div>
            )}
          </div>
        )}

        {/* Services */}
        {(provider.telehealth || provider.sameDay || provider.onlineBooking || provider.website) && (
          <div style={s.detailSection}>
            <div style={s.detailSectionLabel}>Services</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '6px' }}>
              {provider.telehealth && (
                <span style={{ fontSize: '12px', fontWeight: 500, padding: '4px 10px', borderRadius: '20px', background: 'var(--teal-faint)', color: 'var(--teal)' }}>Telehealth available</span>
              )}
              {provider.sameDay && (
                <span style={{ fontSize: '12px', fontWeight: 500, padding: '4px 10px', borderRadius: '20px', background: 'var(--teal-faint)', color: 'var(--teal)' }}>Same-day appointments</span>
              )}
              {provider.onlineBooking && (
                <span style={{ fontSize: '12px', fontWeight: 500, padding: '4px 10px', borderRadius: '20px', background: 'var(--teal-faint)', color: 'var(--teal)' }}>Online booking</span>
              )}
              {provider.website && (
                <a href={provider.website} target="_blank" rel="noreferrer" style={{ fontSize: '12px', fontWeight: 500, padding: '4px 10px', borderRadius: '20px', background: 'var(--cream-2)', color: 'var(--ink-2)', textDecoration: 'none' }}>Visit website ↗</a>
              )}
            </div>
          </div>
        )}

        {/* Hours & location */}
        {(provider.address || provider.hours || Object.values(provider.allHours || {}).some(Boolean)) && (
          <div style={s.detailSection}>
            <div style={s.detailSectionLabel}>Location & hours</div>
            {provider.address && (
              <div style={s.infoRow}>
                <svg width="15" height="15" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, color: 'var(--ink-3)', marginTop: '2px' }}>
                  <path d="M7 0C4.24 0 2 2.24 2 5c0 3.75 5 9 5 9s5-5.25 5-9c0-2.76-2.24-5-5-5zm0 6.5C6.17 6.5 5.5 5.83 5.5 5S6.17 3.5 7 3.5 8.5 4.17 8.5 5 7.83 6.5 7 6.5z" fill="currentColor"/>
                </svg>
                <div style={s.infoText}>{provider.address}</div>
              </div>
            )}
            {provider.hours && (
              <div style={s.infoRow}>
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: 'var(--ink-3)' }}>
                  <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2"/>
                  <path d="M8 5v3.5l2 2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                </svg>
                <div style={s.infoText}>{provider.hours}</div>
              </div>
            )}
            {!provider.hours && Object.values(provider.allHours || {}).some(Boolean) && (
              <div style={{ marginTop: '8px' }}>
                {WEEK_ORDER.map(day => {
                  const h = provider.allHours[day];
                  const isToday = DAY_KEYS[new Date().getDay()] === day;
                  return (
                    <div key={day} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px', fontWeight: isToday ? 600 : 400, color: isToday ? 'var(--teal)' : 'var(--ink-2)' }}>
                      <span style={{ minWidth: '80px' }}>{DAY_FULL[day]}</span>
                      <span style={{ color: h === 'Closed' ? 'var(--ink-4)' : (isToday ? 'var(--teal)' : 'var(--ink-2)') }}>{h || 'Closed'}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* About */}
        {hasRichDetail && (
          <div style={s.detailSection}>
            <div style={s.detailSectionLabel}>About</div>
            <p style={s.description}>{provider.description}</p>
          </div>
        )}

        {/* Who can use this */}
        {provider.whoQualifies && (
          <div style={s.detailSection}>
            <div style={s.detailSectionLabel}>Who can use this</div>
            <div style={s.whoList}>
              {provider.whoQualifies.map((q, i) => (
                <div key={i} style={s.whoItem}>
                  <div style={s.whoDot} />
                  {q}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* What to bring */}
        {provider.whatToBring && (
          <div style={s.detailSection}>
            <div style={s.detailSectionLabel}>What to bring</div>
            <div style={s.whoList}>
              {provider.whatToBring.map((q, i) => (
                <div key={i} style={s.whoItem}>
                  <div style={s.whoDot} />
                  {q}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Insurance & cost */}
        {provider.insuranceNote && (
          <div style={s.detailSection}>
            <div style={s.detailSectionLabel}>Insurance & cost</div>
            <div style={s.infoRow}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: 'var(--ink-3)' }}>
                <rect x="1" y="4" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M1 8h14" stroke="currentColor" strokeWidth="1.2"/>
              </svg>
              <div style={s.infoText}>{provider.insuranceNote}</div>
            </div>
          </div>
        )}

        {/* Providers at this practice */}
        {linkedProviders.length > 0 && (
          <div style={{ ...s.detailSection, borderBottom: 'none' }}>
            <div style={s.detailSectionLabel}>Providers here · {linkedProviders.length}</div>
            <div style={s.providerList}>
              {linkedProviders.map(p => (
                <div key={p.id} style={s.providerRow}>
                  <div style={s.providerAvatar}>
                    {toTitleCase(p.name).split(' ').slice(0, 2).map(w => w[0]).join('')}
                  </div>
                  <div style={s.providerInfo}>
                    <div style={s.providerName}>{toTitleCase(p.name)}</div>
                    {p.specialty && <div style={s.providerSpecialty}>{p.specialty}</div>}
                  </div>
                  {p.accepting_new_patients === true && (
                    <span style={s.providerAccepting}>Accepting</span>
                  )}
                  {p.accepting_new_patients === false && (
                    <span style={s.providerNotAccepting}>Not accepting</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ height: '32px' }} />
      </div>
    </div>
  );
}

// ─── Main screen ───────────────────────────────────────────────────────────────
export default function FindCareScreen({ navigate, goBack, providerContext, user, onAuthPrompt }) {
  const [st, setSt]                   = useState(getState());
  const [filter, setFilter]           = useState(() => getInitialFilter(providerContext));
  const [search]                       = useState('');
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [userLoc, setUserLoc]         = useState(null);
  const [sortBy, setSortBy]           = useState('distance');
  const [openNow, setOpenNow]         = useState(false);
  const [selectedSpecialties, setSelectedSpecialties] = useState([]); // match strings
  const [showSpecialtiesSheet, setShowSpecialtiesSheet] = useState(false);
  const [showFiltersSheet, setShowFiltersSheet]         = useState(false);
  const [distanceRadius, setDistanceRadius]             = useState(null); // miles or null=all
  const [manualTelehealth, setManualTelehealth]         = useState(false);
  const [manualSameDay, setManualSameDay]               = useState(false);

  // AI search state
  const [aiQuery,   setAiQuery]   = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiBanner,  setAiBanner]  = useState(null);
  const [aiCaveat,  setAiCaveat]  = useState(null);
  const [aiExtras,  setAiExtras]  = useState({ telehealth: false, pediatric: false, same_day: false, online_booking: false });
  const aiInputRef = useRef(null);

  // Provider data
  const [allFetched, setAllFetched]   = useState([]);
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);
  const [totalCount, setTotalCount]   = useState(0);
  const [loading, setLoading]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);
  const [savedProviders, setSavedProviders] = useState(() => loadSavedProviders());
  const [savePromptProvider, setSavePromptProvider] = useState(null);

  useEffect(() => subscribe(s => setSt({ ...s })), []);

  // Request geolocation once on mount
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { timeout: 8000, maximumAge: 60000 }
    );
  }, []);

  // Fetch providers — re-runs on filter change or when geo resolves
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setAllFetched([]);
      setDisplayCount(PAGE_SIZE);
      setTotalCount(0);
      setUsingFallback(false);

      // Merge AI-driven extras with manual filter toggles
      const mergedExtras = {
        ...aiExtras,
        telehealth: aiExtras.telehealth || manualTelehealth,
        same_day:   aiExtras.same_day   || manualSameDay,
      };

      try {
        if (userLoc) {
          const [geoResult, unlocatedResult] = await Promise.all([
            buildGeoQuery(filter, userLoc, selectedSpecialties, mergedExtras),
            buildUnlocatedQuery(filter, selectedSpecialties, mergedExtras),
          ]);
          if (cancelled) return;
          if (geoResult.error) throw geoResult.error;
          const located   = (geoResult.data || []).map(adaptRow);
          const unlocated = (unlocatedResult.data || []).map(adaptRow);
          const locatedIds = new Set(located.map(p => p.id));
          const combined  = [...located, ...unlocated.filter(p => !locatedIds.has(p.id))];
          setAllFetched(disambiguateNames(combined));
        } else {
          const { data, count, error } = await buildPageQuery(filter, 0, selectedSpecialties, mergedExtras);
          if (cancelled) return;
          if (error) throw error;
          setAllFetched(disambiguateNames((data || []).map(adaptRow)));
          setTotalCount(count || 0);
        }
      } catch (err) {
        if (cancelled) return;
        console.warn('FindCareScreen: Supabase unavailable, using fallback', err?.message);
        let fallback = FALLBACK_PROVIDERS;
        if (filter === 'common')    fallback = fallback.filter(p => ['primary', 'urgent', 'er'].includes(p.category));
        else if (filter === 'urgent-er') fallback = fallback.filter(p => p.category === 'urgent' || p.category === 'er');
        else if (filter !== 'all') fallback = fallback.filter(p => p.category === filter);
        setAllFetched(fallback);
        setUsingFallback(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [filter, userLoc, selectedSpecialties, aiExtras, manualTelehealth, manualSameDay]);

  async function loadMore() {
    if (loadingMore || usingFallback) return;
    if (userLoc) {
      setDisplayCount(c => c + PAGE_SIZE);
      return;
    }
    setLoadingMore(true);
    try {
      const { data, error } = await buildPageQuery(filter, allFetched.length, selectedSpecialties, aiExtras);
      if (!error && data) setAllFetched(prev => [...prev, ...data.map(adaptRow)]);
    } finally {
      setLoadingMore(false);
    }
  }

  function toggleSave(provider) {
    if (!user) {
      setSavePromptProvider(provider);
      return;
    }
    setSavedProviders(prev => {
      const isAlreadySaved = prev.some(p => p.id === provider.id);
      const updated = isAlreadySaved
        ? prev.filter(p => p.id !== provider.id)
        : [...prev, provider];
      persistSavedProviders(updated);
      return updated;
    });
  }

  async function handleAiSearch(rawQuery) {
    const q = (rawQuery || aiQuery).trim();
    if (!q || aiLoading) return;
    setAiLoading(true);
    try {
      // Pass icd10 from triage providerContext as a routing hint (hidden from user)
      const icd10Hint = providerContext?.icd10 || null;
      const result = await interpretSearch(q, icd10Hint);
      const filters = result.filters || (result.filter ? [result.filter] : ['all']);
      const specialtyMatches = result.specialtyMatches || [];

      if (filters.length === 1 && filters[0] !== 'all') {
        // Single filter — use normal pill-based filter
        setFilter(filters[0]);
        setSelectedSpecialties(specialtyMatches);
        setAiExtras({ telehealth: !!result.telehealth, pediatric: !!result.pediatric, same_day: !!result.same_day, online_booking: !!result.online_booking, categories: null });
        setOpenNow(!!result.open_now);
      } else if (filters.length > 1) {
        // Multi-category — map to DB category values and pass via extras
        const dbCategories = filters.map(f => FILTER_TO_CATEGORY[f]).filter(Boolean);
        setFilter('all');
        setSelectedSpecialties(specialtyMatches);
        setAiExtras({ telehealth: !!result.telehealth, pediatric: !!result.pediatric, same_day: !!result.same_day, online_booking: !!result.online_booking, categories: dbCategories });
        setOpenNow(!!result.open_now);
      } else {
        setFilter('all');
        setSelectedSpecialties([]);
        setAiExtras({ telehealth: !!result.telehealth, pediatric: !!result.pediatric, same_day: !!result.same_day, online_booking: !!result.online_booking, categories: null });
        setOpenNow(!!result.open_now);
      }

      setAiBanner(result.contextBanner || null);
      setAiCaveat(result.caveat || null);
    } catch {
      setAiBanner(null);
    } finally {
      setAiLoading(false);
    }
  }

  function clearAiSearch() {
    setAiQuery('');
    setAiBanner(null);
    setAiCaveat(null);
    setAiExtras({ telehealth: false, pediatric: false, same_day: false, online_booking: false, categories: null });
    setOpenNow(false);
    setFilter('all');
    setSelectedSpecialties([]);
    aiInputRef.current?.focus();
  }

  const { carrier, planType } = st;
  const initialFilter = getInitialFilter(providerContext);
  const isUrgentCtx     = providerContext?.resultLevel === 'urgent';
  const isSoonCtx       = providerContext?.resultLevel === 'soon' && providerContext?.careType !== 'specialist';
  const isSpecialistCtx = providerContext?.careType === 'specialist';
  const showBanner      = !!providerContext && !bannerDismissed;

  // Show inline detail view
  if (selectedProvider) {
    return (
      <DetailView
        provider={selectedProvider}
        carrier={carrier}
        planType={planType}
        onBack={() => setSelectedProvider(null)}
      />
    );
  }

  const primaryPills = initialFilter === 'urgent-er'
    ? [{ id: 'urgent-er', label: 'Urgent + ER' }, ...PRIMARY_PILLS]
    : PRIMARY_PILLS;

  // Distances: DB providers use lat/lng from row; fallback providers already have lat/lng
  const distances = userLoc
    ? Object.fromEntries(allFetched.map(p => [p.id, getProviderDistance(p, userLoc)]))
    : null;

  const sheetFiltersCount = [
    RARE_IDS.has(filter),
    sortBy !== 'distance',
    openNow,
    distanceRadius !== null,
    manualTelehealth,
    manualSameDay,
  ].filter(Boolean).length;

  const isGeoMode = !!userLoc && !usingFallback;
  const searched  = applySearch(allFetched, search);
  const openFiltered = openNow ? searched.filter(p => p.openNow === true) : searched;
  const radiusFiltered = (distanceRadius && distances)
    ? openFiltered.filter(p => { const d = distances[p.id]; return d == null || d <= distanceRadius; })
    : openFiltered;
  const sorted    = sortProviders(radiusFiltered, distances, sortBy);
  const displayed = filter === 'saved'
    ? savedProviders
    : isGeoMode ? sorted.slice(0, displayCount) : sorted;
  const featured  = filter === 'saved' ? [] : displayed.filter(p => p.featured);
  const regular   = filter === 'saved' ? savedProviders : displayed.filter(p => !p.featured);

  const hasMore = filter === 'saved' ? false
    : usingFallback ? false
    : isGeoMode ? displayCount < sorted.length
    : allFetched.length < totalCount;

  const countText = filter === 'saved' ? null
    : allFetched.length === 0 ? null
    : isGeoMode
      ? `Showing ${displayed.length} of ${sorted.length} providers near you`
      : totalCount > allFetched.length
        ? `Showing ${allFetched.length} of ${totalCount} providers`
        : `${allFetched.length} provider${allFetched.length !== 1 ? 's' : ''}`;

  const showReferralBanner = filter === 'specialist';
  const isRestrictivePlan  = planType === 'hmo' || planType === 'epo';
  const carrierName        = CARRIER_DISPLAY[carrier] || 'your carrier';
  const planName           = PLAN_DISPLAY[planType] || '';

  const subtitle = carrier && planType
    ? `Greenville, SC · ${CARRIER_DISPLAY[carrier] || carrier} ${PLAN_DISPLAY[planType] || planType}`
    : 'Greenville, SC';

  return (
    <div style={s.screen}>

      {/* Header */}
      <div style={s.header}>
        <div style={s.accentBar} />
        <button style={s.back} onClick={goBack}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 4L6 8l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>
        <div style={s.title}>
          {isUrgentCtx
            ? <><em style={{ fontStyle: 'italic', color: '#c0392b' }}>Get care</em> now</>
            : <>Find <em style={{ fontStyle: 'italic', color: 'var(--teal)' }}>care</em></>
          }
        </div>
        <div style={s.subtitle}>{subtitle}</div>
      </div>

      {/* Context banner */}
      {showBanner && (
        <div style={{
          ...s.banner,
          background: isUrgentCtx ? '#fef3f0' : 'var(--teal-faint)',
          borderBottomColor: isUrgentCtx ? 'rgba(192,57,43,0.15)' : 'rgba(30,92,84,0.15)',
          padding: '10px 16px',
        }}>
          <div style={{ ...s.bannerDot, background: isUrgentCtx ? '#c0392b' : 'var(--teal)' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: isUrgentCtx ? '#c0392b' : 'var(--teal)', lineHeight: 1.3 }}>
              {isUrgentCtx
                ? 'Urgent care and ER near you'
                : isSpecialistCtx
                  ? providerContext?.specialtyHint
                    ? `${providerContext.specialtyHint} specialists near you`
                    : 'Specialists near you'
                  : isSoonCtx
                    ? 'Primary care near you'
                    : 'Filtered for your symptoms'
              }
            </div>
            <div style={{ fontSize: '11px', color: isUrgentCtx ? '#c0392b' : 'var(--teal)', opacity: 0.65, marginTop: '2px' }}>
              From your symptom check
            </div>
          </div>
          <button style={s.bannerClear} onClick={() => { setBannerDismissed(true); setFilter('all'); }}>
            Change
          </button>
        </div>
      )}

      {/* AI search bar */}
      <div style={s.searchWrap}>
        <div style={s.searchBox}>
          {aiLoading ? (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, animation: 'cp-spin 0.8s linear infinite' }}>
              <circle cx="7" cy="7" r="5.5" stroke="var(--teal)" strokeWidth="1.5" strokeDasharray="20 10"/>
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
              <path d="M12 2L14.4 9.6L22 12L14.4 14.4L12 22L9.6 14.4L2 12L9.6 9.6L12 2Z" stroke="var(--teal)" strokeWidth="1.6" strokeLinejoin="round"/>
            </svg>
          )}
          <input
            ref={aiInputRef}
            style={s.searchInput}
            value={aiQuery}
            onChange={e => setAiQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAiSearch(); } }}
            placeholder="Describe what you need…"
            disabled={aiLoading}
          />
          {aiQuery.length > 0 && !aiLoading && (
            <button style={s.searchClear} onClick={clearAiSearch}>✕</button>
          )}
          {aiQuery.length > 0 && !aiLoading && (
            <button style={s.aiSearchBtn} onClick={() => handleAiSearch()}>
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                <path d="M14 8L2 2l2.5 6L2 14l12-6z" fill="currentColor"/>
              </svg>
            </button>
          )}
        </div>
        {userLoc && (
          <div style={s.locBadge}>
            <svg width="10" height="12" viewBox="0 0 10 12" fill="none" style={{ flexShrink: 0 }}>
              <path d="M5 0C2.24 0 0 2.24 0 5c0 3.75 5 7 5 7s5-3.25 5-7C10 2.24 7.76 0 5 0zm0 6.5C4.17 6.5 3.5 5.83 3.5 5S4.17 3.5 5 3.5 6.5 4.17 6.5 5 5.83 6.5 5 6.5z" fill="currentColor"/>
            </svg>
            Using your location · sorted by distance
          </div>
        )}
      </div>

      {/* AI search result banner */}
      {aiBanner && (
        <div style={s.aiBanner}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: '1px' }}>
            <path d="M12 2L14.4 9.6L22 12L14.4 14.4L12 22L9.6 14.4L2 12L9.6 9.6L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
          </svg>
          <div style={{ flex: 1 }}>
            <div style={s.aiBannerText}>{aiBanner}</div>
            {aiCaveat && <div style={s.aiCaveatText}>{aiCaveat}</div>}
          </div>
          <button style={s.bannerClear} onClick={clearAiSearch}>Clear</button>
        </div>
      )}

      <style>{`@keyframes cp-spin { to { transform: rotate(360deg); } } @keyframes cp-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>

      {/* Filter pills */}
      <div style={s.pillsRow}>
        <button
          style={{ ...s.pill, ...(sheetFiltersCount > 0 ? s.pillActive : s.pillInactive), display: 'flex', alignItems: 'center', gap: '5px' }}
          onClick={() => setShowFiltersSheet(true)}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M1 3h10M3 6h6M5 9h2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
          {sheetFiltersCount > 0 ? `Filters (${sheetFiltersCount})` : 'Filters'}
        </button>
        {primaryPills.map(pill => {
          const isRareActive = RARE_IDS.has(filter);
          const isActive = !isRareActive && filter === pill.id;
          return (
            <button
              key={pill.id}
              style={{ ...s.pill, ...(isActive ? s.pillActive : s.pillInactive) }}
              onClick={() => {
                setAiBanner(null); setAiCaveat(null); setAiExtras({ telehealth: false, pediatric: false, categories: null }); setAiQuery(''); setOpenNow(false);
                if (pill.id === 'specialist') {
                  setFilter('specialist');
                  setShowSpecialtiesSheet(true);
                  setSelectedProvider(null);
                } else {
                  setFilter(pill.id);
                  setSelectedSpecialties([]);
                  setSelectedProvider(null);
                }
              }}
            >
              {pill.label}
              {pill.id === 'specialist' && selectedSpecialties.length > 0 && ` (${selectedSpecialties.length})`}
            </button>
          );
        })}
      </div>

      {/* No-insurance prompt */}
      {!carrier && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', background: 'var(--amber-light)', borderBottom: '1px solid rgba(196,114,42,0.18)', flexShrink: 0 }}>
          <svg width="14" height="11" viewBox="0 0 14 11" fill="none" style={{ flexShrink: 0 }}>
            <rect x="0.5" y="0.5" width="13" height="10" rx="1.5" stroke="var(--amber)" strokeWidth="1.2"/>
            <path d="M0.5 4h13" stroke="var(--amber)" strokeWidth="1.2"/>
          </svg>
          <span style={{ flex: 1, fontSize: '11.5px', color: 'var(--ink-2)', lineHeight: 1.4 }}>Add your insurance to see which providers are in-network.</span>
          <button
            style={{ fontSize: '12px', fontWeight: 600, color: 'var(--amber)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', flexShrink: 0, padding: 0, whiteSpace: 'nowrap' }}
            onClick={() => navigate?.('insurance')}
          >
            Add coverage →
          </button>
        </div>
      )}

      {/* Referral banner */}
      {showReferralBanner && (
        <div style={s.referralBanner}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, marginTop: '1px' }}>
            <circle cx="7" cy="7" r="6" stroke="var(--amber)" strokeWidth="1.3"/>
            <path d="M7 4v3.5M7 9.5v.5" stroke="var(--amber)" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
          <span style={s.referralText}>
            {isRestrictivePlan
              ? `Your ${planName} plan requires a referral from your primary care doctor before seeing a specialist.`
              : planType === 'ppo'
                ? `Referral may be required. With your ${carrierName} PPO you can often self-refer, but confirm with your plan documents.`
                : 'Specialist visits may require a referral. Check your plan documents or call member services to confirm.'
            }
          </span>
        </div>
      )}

      {/* Results body */}
      {loading ? (
        <div style={s.body}>
          <div style={s.bodyInner}>
            {[160, 180, 140].map((w, i) => (
              <div key={i} style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '14px 14px 0', overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                  <div>
                    <div style={{ ...s.shimmer, width: `${w}px`, height: '15px', borderRadius: '6px', marginBottom: '7px' }} />
                    <div style={{ ...s.shimmer, width: `${w - 50}px`, height: '11px', borderRadius: '6px' }} />
                  </div>
                  <div style={{ ...s.shimmer, width: '64px', height: '22px', borderRadius: '100px' }} />
                </div>
                <div style={{ ...s.shimmer, width: '130px', height: '13px', borderRadius: '6px', marginBottom: '10px' }} />
                <div style={{ display: 'flex', gap: '6px', marginBottom: '14px' }}>
                  <div style={{ ...s.shimmer, width: '52px', height: '20px', borderRadius: '100px' }} />
                  <div style={{ ...s.shimmer, width: `${70 + i * 14}px`, height: '20px', borderRadius: '100px' }} />
                </div>
                <div style={{ display: 'flex', borderTop: '1px solid var(--border)', height: '36px' }} />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={s.body}>
          <div style={s.bodyInner}>
            {countText && <div style={s.countBar}>{countText}</div>}

            {displayed.length === 0 ? (
              <div style={s.empty}>
                <div style={s.emptyTitle}>{filter === 'saved' ? 'No saved providers' : 'No matches nearby'}</div>
                <div style={s.emptyText}>{filter === 'saved' ? 'Tap the heart on any provider card to save them here.' : 'Try clearing a filter or searching by name.'}</div>
                {filter !== 'saved' && filter !== 'all' && (
                  <button
                    style={{ marginTop: '14px', padding: '8px 18px', background: 'none', border: '1px solid var(--border)', borderRadius: '100px', fontSize: '12px', fontWeight: 600, color: 'var(--ink-3)', cursor: 'pointer', fontFamily: 'var(--font-body)' }}
                    onClick={() => setFilter('all')}
                  >
                    Show all providers
                  </button>
                )}
              </div>
            ) : (
              <>
                {featured.length > 0 && (
                  <>
                    <div style={s.sectionLabel}>Featured · accepting new patients</div>
                    {featured.map(p => (
                      <ProviderCard key={p.id} provider={p} carrier={carrier} planType={planType} onTap={() => setSelectedProvider(p)} distance={distances?.[p.id] ?? null} isSaved={savedProviders.some(s => s.id === p.id)} onToggleSave={toggleSave} />
                    ))}
                  </>
                )}
                {regular.length > 0 && (
                  <>
                    {featured.length > 0 && filter !== 'saved' && <div style={s.sectionLabel}>All providers</div>}
                    {filter === 'saved' && <div style={s.sectionLabel}>Saved providers</div>}
                    {regular.map(p => (
                      <ProviderCard key={p.id} provider={p} carrier={carrier} planType={planType} onTap={() => setSelectedProvider(p)} distance={distances?.[p.id] ?? null} isSaved={savedProviders.some(sv => sv.id === p.id)} onToggleSave={toggleSave} />
                    ))}
                  </>
                )}
              </>
            )}

            {hasMore && (
              <button style={s.loadMoreBtn} onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading…' : 'Load more providers'}
              </button>
            )}

            <div style={{ height: '8px' }} />
          </div>
        </div>
      )}

      {/* Filters bottom sheet */}
      {showFiltersSheet && (
        <div style={s.drawerOverlay} onClick={() => setShowFiltersSheet(false)}>
          <div style={{ ...s.drawer, maxHeight: '80dvh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={s.drawerHandle} />
            <div style={{ ...s.drawerTitle, fontSize: '18px', marginBottom: '20px' }}>Filters</div>

            {/* Sort */}
            <div style={{ marginBottom: '20px' }}>
              <div style={s.sheetGroupLabel}>Sort by</div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                {[{ id: 'distance', label: userLoc ? 'Nearest first' : 'Default' }, { id: 'name', label: 'A–Z' }].map(opt => (
                  <button key={opt.id}
                    style={{ ...s.sheetPill, flex: 1, textAlign: 'center', ...(sortBy === opt.id ? s.sheetPillActive : s.sheetPillInactive) }}
                    onClick={() => setSortBy(opt.id)}
                  >
                    {sortBy === opt.id && '✓ '}{opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Open now + service filters */}
            <div style={{ marginBottom: '20px' }}>
              <div style={s.sheetGroupLabel}>Availability</div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                <button
                  style={{ ...s.sheetPill, ...(openNow ? s.sheetPillActive : s.sheetPillInactive) }}
                  onClick={() => setOpenNow(v => !v)}
                >
                  {openNow && '✓ '}Open now only
                </button>
                <button
                  style={{ ...s.sheetPill, ...(manualSameDay ? s.sheetPillActive : s.sheetPillInactive) }}
                  onClick={() => setManualSameDay(v => !v)}
                >
                  {manualSameDay && '✓ '}Same-day
                </button>
              </div>
            </div>

            {/* Services */}
            <div style={{ marginBottom: '20px' }}>
              <div style={s.sheetGroupLabel}>Services</div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                <button
                  style={{ ...s.sheetPill, ...(manualTelehealth ? s.sheetPillActive : s.sheetPillInactive) }}
                  onClick={() => setManualTelehealth(v => !v)}
                >
                  {manualTelehealth && '✓ '}Telehealth
                </button>
              </div>
            </div>

            {/* Distance radius */}
            {userLoc && (
              <div style={{ marginBottom: '20px' }}>
                <div style={s.sheetGroupLabel}>Distance</div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                  {[5, 10, 25, 50].map(mi => (
                    <button key={mi}
                      style={{ ...s.sheetPill, ...(distanceRadius === mi ? s.sheetPillActive : s.sheetPillInactive) }}
                      onClick={() => setDistanceRadius(prev => prev === mi ? null : mi)}
                    >
                      {distanceRadius === mi && '✓ '}Within {mi} mi
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* More categories */}
            <div style={{ marginBottom: '20px' }}>
              <div style={s.sheetGroupLabel}>More categories</div>
              <div style={{ ...s.sheetPills, marginTop: '8px' }}>
                {RARE_CATEGORIES.map(cat => {
                  const active = filter === cat.id;
                  return (
                    <button key={cat.id}
                      style={{ ...s.sheetPill, ...(active ? s.sheetPillActive : s.sheetPillInactive) }}
                      onClick={() => { setFilter(active ? 'all' : cat.id); setShowFiltersSheet(false); }}
                    >
                      {active && '✓ '}{cat.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <button style={s.drawerBtn} onClick={() => setShowFiltersSheet(false)}>Done</button>
            {sheetFiltersCount > 0 && (
              <button style={s.drawerSkip} onClick={() => {
                setSortBy('distance'); setOpenNow(false); setDistanceRadius(null);
                setManualTelehealth(false); setManualSameDay(false);
                if (RARE_IDS.has(filter)) setFilter('all');
                setShowFiltersSheet(false);
              }}>
                Clear all filters
              </button>
            )}
          </div>
        </div>
      )}

      {/* Specialists bottom sheet */}
      {showSpecialtiesSheet && (
        <div style={s.drawerOverlay} onClick={() => setShowSpecialtiesSheet(false)}>
          <div style={{ ...s.drawer, maxHeight: '75dvh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={s.drawerHandle} />
            <div style={{ ...s.drawerTitle, fontSize: '18px', marginBottom: '16px' }}>Select specialties</div>
            {SPECIALTY_GROUPS.map(({ group, specialties }) => (
              <div key={group} style={{ marginBottom: '16px' }}>
                <div style={s.sheetGroupLabel}>{group}</div>
                <div style={s.sheetPills}>
                  {specialties.map(({ label, match }) => {
                    const active = selectedSpecialties.includes(match);
                    return (
                      <button
                        key={match}
                        style={{ ...s.sheetPill, ...(active ? s.sheetPillActive : s.sheetPillInactive) }}
                        onClick={() => setSelectedSpecialties(prev =>
                          active ? prev.filter(m => m !== match) : [...prev, match]
                        )}
                      >
                        {active && '✓ '}{label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <button style={{ ...s.drawerBtn, marginTop: '4px' }} onClick={() => setShowSpecialtiesSheet(false)}>
              {selectedSpecialties.length > 0 ? `View results` : 'View all specialists'}
            </button>
            {selectedSpecialties.length > 0 && (
              <button style={s.drawerSkip} onClick={() => { setSelectedSpecialties([]); setShowSpecialtiesSheet(false); }}>
                Clear selection
              </button>
            )}
          </div>
        </div>
      )}

      {/* Save provider — auth prompt for anonymous users */}
      {savePromptProvider && (
        <div style={s.drawerOverlay} onClick={() => setSavePromptProvider(null)}>
          <div style={s.drawer} onClick={e => e.stopPropagation()}>
            <div style={s.drawerHandle} />
            <div style={s.drawerTitle}>Save {savePromptProvider.name}?</div>
            <p style={s.drawerText}>Sign in to save providers and find them quickly when you need care.</p>
            <button style={s.drawerBtn} onClick={() => { setSavePromptProvider(null); onAuthPrompt?.(); }}>
              Sign in to save
            </button>
            <button style={s.drawerSkip} onClick={() => setSavePromptProvider(null)}>
              Not now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────────
const s = {
  screen:         { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--cream)', position: 'relative' },
  header:         { padding: '52px 20px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', position: 'relative', flexShrink: 0 },
  detailHeader:   { padding: '52px 20px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', position: 'relative', flexShrink: 0 },
  accentBar:      { position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, var(--teal), var(--teal-2))' },
  back:           { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 500, color: 'var(--ink-3)', cursor: 'pointer', marginBottom: '14px', background: 'none', border: 'none', padding: 0, fontFamily: 'var(--font-body)' },
  title:          { fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: '26px', color: 'var(--ink)', letterSpacing: '-0.5px', lineHeight: 1.2 },
  subtitle:       { fontSize: '12px', color: 'var(--ink-4)', marginTop: '4px', fontWeight: 500 },
  banner:         { display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 16px', borderBottom: '1px solid', flexShrink: 0 },
  bannerDot:      { width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0 },
  bannerText:     { flex: 1, fontSize: '11px', fontWeight: 600 },
  bannerClear:    { fontSize: '11px', color: 'var(--ink-4)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', flexShrink: 0, padding: 0 },
  searchWrap:     { padding: '10px 16px 0', background: 'var(--surface)', flexShrink: 0 },
  locBadge:       { display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 2px 10px', fontSize: '11px', fontWeight: 500, color: 'var(--teal)' },
  searchBox:      { display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--cream)', borderRadius: 'var(--radius-md)', padding: '10px 14px', border: '1px solid var(--border)' },
  searchInput:    { flex: 1, fontSize: '13px', color: 'var(--ink)', background: 'none', border: 'none', outline: 'none', fontFamily: 'var(--font-body)' },
  searchClear:    { fontSize: '11px', color: 'var(--ink-4)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'var(--font-body)' },
  aiSearchBtn:    { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', background: 'var(--teal)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', flexShrink: 0 },
  aiBanner:       { display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '8px 16px', background: 'var(--teal-faint)', borderBottom: '1px solid rgba(30,92,84,0.12)', flexShrink: 0 },
  aiBannerText:   { fontSize: '12px', fontWeight: 600, color: 'var(--teal)', lineHeight: 1.4 },
  aiCaveatText:   { fontSize: '11px', color: 'var(--teal)', opacity: 0.75, marginTop: '2px', lineHeight: 1.4 },
  pillsRow:       { display: 'flex', gap: '6px', padding: '10px 16px', overflowX: 'auto', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flexShrink: 0, msOverflowStyle: 'none', scrollbarWidth: 'none' },
  pill:           { padding: '5px 12px', borderRadius: '100px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0, cursor: 'pointer', fontFamily: 'var(--font-body)', border: 'none' },
  pillActive:     { background: 'var(--teal)', color: 'white' },
  pillInactive:   { background: 'white', color: 'var(--ink-3)', border: '1px solid var(--border)' },
  sortRow:        { display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flexShrink: 0 },
  sortLabel:      { fontSize: '11px', color: 'var(--ink-4)', fontWeight: 500, marginRight: '2px' },
  sortBtn:        { padding: '4px 10px', borderRadius: '100px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)', background: 'none', border: '1px solid var(--border)', color: 'var(--ink-3)' },
  sortBtnActive:  { background: 'var(--teal-faint)', color: 'var(--teal)', borderColor: 'var(--teal)' },
  sortBtnOpenNow: { background: '#f0faf5', color: '#1a7a4a', borderColor: '#1a7a4a' },
  referralBanner: { display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '10px 16px', background: 'var(--amber-light)', borderBottom: '1px solid rgba(196,114,42,0.2)', flexShrink: 0 },
  referralText:   { fontSize: '11.5px', color: 'var(--ink-2)', lineHeight: 1.5, flex: 1 },
  body:           { flex: 1, minHeight: 0, overflowY: 'auto', msOverflowStyle: 'none', scrollbarWidth: 'none', display: 'flex', flexDirection: 'column' },
  bodyInner:      { display: 'flex', flexDirection: 'column', gap: '10px', padding: '14px 16px 0' },
  countBar:       { fontSize: '11px', color: 'var(--ink-4)', fontWeight: 500, paddingBottom: '2px' },
  loadingWrap:    { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: '14px', padding: '40px 0' },
  loadingDots:    { display: 'flex', gap: '8px' },
  loadingDot:     { width: '9px', height: '9px', borderRadius: '50%', background: 'var(--teal)', opacity: 0.7 },
  loadingText:    { fontSize: '13px', color: 'var(--ink-3)', fontWeight: 500 },
  loadMoreBtn:    { width: '100%', padding: '14px', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', fontSize: '13px', fontWeight: 600, color: 'var(--teal)', cursor: 'pointer', fontFamily: 'var(--font-body)', marginTop: '4px' },
  sectionLabel:   { fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-4)', paddingTop: '4px' },
  empty:          { padding: '40px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' },
  emptyTitle:     { fontFamily: 'var(--font-display)', fontSize: '17px', color: 'var(--ink-3)' },
  emptyText:      { fontSize: '13px', color: 'var(--ink-4)' },
  // Card
  card:           { background: 'var(--surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' },
  cardFeatured:   { border: '1px solid rgba(30,92,84,0.3)' },
  featuredBar:    { background: 'var(--teal)', padding: '4px 12px', display: 'flex', alignItems: 'center', gap: '6px' },
  featuredDot:    { width: '5px', height: '5px', borderRadius: '50%', background: 'rgba(255,255,255,0.5)', flexShrink: 0 },
  featuredLabel:  { fontSize: '9px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'white' },
  cardBody:       { padding: '12px 14px 10px', cursor: 'pointer' },
  cardTop:        { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' },
  cardLeft:       { flex: 1, minWidth: 0 },
  cardName:       { fontSize: '15px', fontFamily: 'var(--font-display)', fontWeight: 500, color: 'var(--ink)', lineHeight: 1.25, marginBottom: '2px', letterSpacing: '-0.2px' },
  cardSpecialty:  { fontSize: '11px', color: 'var(--ink-4)', marginBottom: '4px' },
  cardAddress:    { fontSize: '11px', color: 'var(--ink-3)', marginBottom: '8px' },
  statusBadge:    { fontSize: '11px', fontWeight: 600, padding: '4px 9px', borderRadius: '100px', whiteSpace: 'nowrap', flexShrink: 0 },
  shimmer:        { background: 'linear-gradient(90deg, var(--cream-2) 25%, var(--cream-3) 50%, var(--cream-2) 75%)', backgroundSize: '200% 100%', animation: 'cp-shimmer 1.5s ease-in-out infinite' },
  insMatch:       { display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '8px' },
  insCheck:       { width: '14px', height: '14px', borderRadius: '50%', background: 'var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  insLabel:       { fontSize: '11px', color: 'var(--teal)', fontWeight: 500 },
  tags:           { display: 'flex', gap: '4px', flexWrap: 'wrap' },
  tag:            { fontSize: '9.5px', fontWeight: 600, padding: '2px 7px', borderRadius: '100px' },
  tagDistance:    { fontSize: '9.5px', fontWeight: 600, padding: '2px 7px', borderRadius: '100px', background: 'var(--cream-2)', color: 'var(--ink-3)' },
  tagReferral:    { fontSize: '9.5px', fontWeight: 600, padding: '2px 7px', borderRadius: '100px', background: '#fdf0e6', color: 'var(--amber)' },
  tagClickable:   { cursor: 'pointer', border: 'none', fontFamily: 'var(--font-body)' },
  hoursPanel:     { marginTop: '8px', background: 'var(--cream)', borderRadius: 'var(--radius-sm)', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: '3px' },
  hoursRow:       { display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--ink-3)', padding: '1px 0' },
  hoursTodayRow:  { color: 'var(--ink)', fontWeight: 600 },
  hoursDay:       { flex: '0 0 80px' },
  hoursTime:      { flex: 1, textAlign: 'right' },
  cardActions:    { display: 'flex', borderTop: '1px solid var(--border)', alignItems: 'stretch' },
  action:         { flex: 1, padding: '9px 4px', fontSize: '11px', fontWeight: 600, color: 'var(--ink-2)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', textAlign: 'center', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  actionDivider:  { width: '1px', background: 'var(--border)', flexShrink: 0 },
  // Detail view
  detailHero:     { padding: '20px 20px 18px', borderBottom: '1px solid var(--border)', background: 'var(--surface)' },
  categoryLabel:  { fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' },
  detailName:     { fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: '22px', color: 'var(--ink)', letterSpacing: '-0.4px', lineHeight: 1.2, marginBottom: '4px' },
  detailSpecialty:{ fontSize: '13px', color: 'var(--ink-3)', marginBottom: '10px' },
  tagRow:         { display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' },
  detailTag:      { display: 'inline-flex', alignItems: 'center', fontSize: '10px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', padding: '2px 7px', borderRadius: '100px' },
  quickActions:   { display: 'flex', gap: '8px', padding: '14px 20px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', flexWrap: 'wrap' },
  actionPill:     { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '11px 8px', borderRadius: 'var(--radius-md)', fontSize: '13px', fontWeight: 600, cursor: 'pointer', textDecoration: 'none', border: 'none', fontFamily: 'var(--font-body)', minWidth: '80px' },
  detailSection:  { padding: '16px 20px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', marginTop: '8px' },
  detailSectionLabel: { fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: '10px' },
  infoRow:        { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '8px', fontSize: '13.5px' },
  infoText:       { color: 'var(--ink-2)', lineHeight: 1.4, flex: 1 },
  description:    { fontSize: '13.5px', color: 'var(--ink-2)', lineHeight: 1.65, margin: 0 },
  providerList:   { display: 'flex', flexDirection: 'column', gap: '2px' },
  providerRow:    { display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid var(--border)' },
  providerAvatar: { width: '34px', height: '34px', borderRadius: '50%', background: 'var(--teal-faint)', color: 'var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, flexShrink: 0 },
  providerInfo:   { flex: 1, minWidth: 0 },
  providerName:   { fontSize: '13.5px', fontWeight: 500, color: 'var(--ink)', lineHeight: 1.3 },
  providerSpecialty: { fontSize: '11px', color: 'var(--ink-4)', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  providerAccepting:    { fontSize: '10px', fontWeight: 600, padding: '2px 7px', borderRadius: '100px', background: 'var(--teal-faint)', color: 'var(--teal)', flexShrink: 0 },
  providerNotAccepting: { fontSize: '10px', fontWeight: 600, padding: '2px 7px', borderRadius: '100px', background: '#fef3f0', color: '#c0392b', flexShrink: 0 },
  whoList:        { display: 'flex', flexDirection: 'column', gap: '6px' },
  whoItem:        { display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', color: 'var(--ink-2)', lineHeight: 1.4 },
  whoDot:         { width: '5px', height: '5px', borderRadius: '50%', background: 'var(--teal)', flexShrink: 0, marginTop: '6px' },
  // Heart / save
  heartBtn:       { background: 'none', border: 'none', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  // Save auth drawer
  drawerOverlay:  { position: 'absolute', inset: 0, background: 'rgba(26,40,38,0.45)', zIndex: 100, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' },
  drawer:         { background: 'var(--surface)', borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0', padding: '20px 24px 36px', display: 'flex', flexDirection: 'column', gap: '10px' },
  drawerHandle:   { width: '36px', height: '4px', borderRadius: '2px', background: 'var(--border-strong)', alignSelf: 'center', marginBottom: '6px' },
  drawerTitle:    { fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: '20px', color: 'var(--ink)', letterSpacing: '-0.3px' },
  drawerText:     { fontSize: '13.5px', color: 'var(--ink-3)', lineHeight: 1.5, margin: 0 },
  drawerBtn:      { width: '100%', padding: '15px', background: 'var(--teal)', color: 'white', border: 'none', borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)', fontSize: '15px', fontWeight: 600, cursor: 'pointer' },
  drawerSkip:     { width: '100%', padding: '10px', background: 'none', border: 'none', color: 'var(--ink-4)', fontFamily: 'var(--font-body)', fontSize: '13px', cursor: 'pointer' },
  sheetGroupLabel:{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-4)', marginBottom: '8px' },
  sheetPills:     { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  sheetPill:      { padding: '7px 14px', borderRadius: '100px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font-body)' },
  sheetPillActive:  { background: 'var(--teal)', color: 'white', border: '1.5px solid var(--teal)' },
  sheetPillInactive:{ background: 'white', color: 'var(--ink-2)', border: '1.5px solid var(--border)' },
};
